
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle } from "lucide-react";

interface FeedbackFormProps {
  onFeedbackSubmitted?: () => void;
}

const FeedbackForm = ({ onFeedbackSubmitted }: FeedbackFormProps) => {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("You must be logged in to submit feedback");
      return;
    }

    if (!title.trim() || !category || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Submitting feedback for user:", user.id);
      
      const { data, error } = await supabase
        .from("feedback")
        .insert({
          user_id: user.id,
          title: title.trim(),
          category,
          message: message.trim(),
        })
        .select()
        .single();

      if (error) {
        console.error("Error submitting feedback:", error);
        throw error;
      }

      console.log("Feedback submitted successfully:", data);
      
      // Send confirmation email
      try {
        await supabase.functions.invoke('send-notification-email', {
          body: {
            user_id: user.id,
            email_type: 'feedback_response',
            subject: 'We received your feedback! 📝',
            content: {
              title: 'Feedback Received',
              message: `Thank you for submitting your feedback titled "${title.trim()}". Our team will review it and get back to you if needed. You can view your feedback history anytime in the Feedback Center.`,
              action_url: `${window.location.origin}/feedback`,
              action_text: 'View My Feedback'
            }
          }
        });
      } catch (emailError) {
        console.error("Failed to send confirmation email:", emailError);
        // Don't fail the submission for email errors
      }
      
      toast.success("Feedback submitted successfully!");
      setTitle("");
      setCategory("");
      setMessage("");
      setIsSubmitted(true);
      
      // Call the callback if provided
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }

      // Reset the submitted state after 3 seconds
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h3 className="font-poppins text-xl font-semibold text-foreground mb-2">
            Feedback Submitted Successfully!
          </h3>
          <p className="text-muted-foreground font-inter mb-4">
            Thank you for your feedback. We appreciate your input and will review it carefully.
          </p>
          <Button 
            onClick={() => setIsSubmitted(false)}
            className="bg-anyskill-purple hover:bg-anyskill-purple/90"
          >
            Submit Another Feedback
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-poppins text-2xl">Submit Feedback</CardTitle>
        <CardDescription className="font-inter">
          Help us improve by sharing your thoughts and suggestions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title" className="font-inter">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your feedback"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category" className="font-inter">Category</Label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Bug Report">Bug Report</SelectItem>
                <SelectItem value="Feature Request">Feature Request</SelectItem>
                <SelectItem value="General">General</SelectItem>
                <SelectItem value="UI/UX">UI/UX</SelectItem>
                <SelectItem value="Performance">Performance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="font-inter">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Detailed feedback or description"
              rows={6}
              required
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-anyskill-purple hover:bg-anyskill-purple/90"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default FeedbackForm;
